import {createSlice} from '@reduxjs/toolkit';
import {dateTime} from '@gravity-ui/date-utils';

import type {Invitation} from '@yandex-data-ui/cloud-schemas/build/models/organization-manager';

import {createThunk} from '~ui/store/utils';
import {sdk} from '~ui/services/sdk';
import {i18n} from '~ui/components/InviteUserModal/i18n';
import {metrikaEvents} from '~ui/components/InviteUserModal/utils';

import {waitOperationFactory} from '~ui/utils';

import toaster from '~ui/utils/toaster';

const waitOrgOperation = waitOperationFactory(sdk.organizationManager.getOperation);

interface StartInvitePayload {
    emails: string[];
    cloudOrgId: string;
    notAfter?: number;
}

type StatusType = 'init' | 'failed' | 'finish';

interface InviteState {
    status: StatusType;
    validInvites?: Invitation[];
    invalidInvites?: Invitation[];
}

const initialState: InviteState = {
    status: 'init',
};

const oneWeek = 60 * 60 * 24 * 7;

const startInviteUsers = createThunk('invite/start', async (data: StartInvitePayload, thunkAPI) => {
    const sdk = thunkAPI.extra.sdk;
    const {emails, cloudOrgId, notAfter = oneWeek} = data;

    return waitOrgOperation(
        sdk.organizationManager.createInvitation({
            organizationId: cloudOrgId,
            notAfter: {
                seconds: String(dateTime().add(notAfter, 'seconds').unix()),
                nanos: 0,
            },
            invitations: emails.map((email) => ({
                invitee: {
                    email,
                },
            })),
            serviceId: 'wiki',
        }),
    )
        .then((operation) => {
            const validInvites = operation.validInvitations || [];
            const invalidInvites = operation.invalidInvitations || [];

            return {
                status: 'finish',
                validInvites,
                invalidInvites,
            };
        })
        .catch((error) => {
            metrikaEvents.errorInvite();

            toaster.createErrorToast('invite/start', i18n('message-failure'), error);

            return {
                status: 'failed',
            };
        });
});

const {reducer, actions} = createSlice({
    name: 'invite',
    initialState,
    reducers: {},
    extraReducers: (builder) =>
        builder.addCase(startInviteUsers.fulfilled, (state, {payload}) => {
            Object.assign(state, payload);
        }),
});

export {reducer, actions, startInviteUsers};
